import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { listServiceTypesApiService } from '../../api/listServiceTypesApiService';
import type { ServiceTypeListItem } from '../../types/serviceTypeTypes';

const PAGE_SIZE = 10;

export type TabId = 'available' | 'my-requests';

export interface UseAvailableServiceListResult {
  items: ServiceTypeListItem[];
  loading: boolean;
  hasNextPage: boolean;
  isInitialLoading: boolean;
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
  sentinelRef: React.RefObject<HTMLDivElement | null>;
  activeTab: TabId;
  handleTabChange: (tab: TabId) => void;
  openId: string | null;
  toggleAccordion: (id: string) => void;
  handleRequestService: (serviceTypeName: string) => void;
}

export function useAvailableServiceList(): UseAvailableServiceListResult {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const urlTab = searchParams.get('tab');
  const activeTab: TabId =
    urlTab === 'my-requests' ? 'my-requests' : 'available';

  const [items, setItems] = useState<ServiceTypeListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);

  const loadingRef = useRef(false);
  const pageRef = useRef(0);
  const initializedRef = useRef(false);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const fetchPage = useCallback(async (page: number) => {
    if (loadingRef.current) {
      return;
    }
    loadingRef.current = true;
    setLoading(true);

    const response = await listServiceTypesApiService.listServiceTypes({
      status: 'active',
      page,
      limit: PAGE_SIZE,
    });

    loadingRef.current = false;
    setLoading(false);
    pageRef.current = page;

    if (page === 1) {
      setItems(response.data);
    } else {
      setItems((prev) => [...prev, ...response.data]);
    }
    setHasNextPage(response.hasNextPage);
  }, []);

  const initialize = useCallback(() => {
    if (initializedRef.current) {
      return;
    }
    initializedRef.current = true;
    fetchPage(1);
  }, [fetchPage]);

  const loadMore = useCallback(() => {
    if (loadingRef.current || !hasNextPage) {
      return;
    }
    fetchPage(pageRef.current + 1);
  }, [hasNextPage, fetchPage]);

  const handleTabChange = useCallback(
    (tab: TabId) => {
      const next = new URLSearchParams();
      next.set('tab', tab);
      setSearchParams(next, { replace: true });
    },
    [setSearchParams]
  );

  const toggleAccordion = useCallback((id: string) => {
    setOpenId((prev) => (prev === id ? null : id));
  }, []);

  const handleRequestService = useCallback(
    (serviceTypeName: string) => {
      navigate(`/Produkty i usługi/${encodeURIComponent(serviceTypeName)}`);
    },
    [navigate]
  );

  // Write ?tab=available on initial load when no tab param is present so the
  // URL always reflects active tab state from the first render onward.
  useEffect(() => {
    if (!searchParams.get('tab')) {
      const next = new URLSearchParams(searchParams);
      next.set('tab', 'available');
      setSearchParams(next, { replace: true });
    }
    // Intentionally runs once on mount only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    initialize();
  }, [initialize]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) {
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          loadMore();
        }
      },
      { threshold: 0.1, root: scrollContainerRef.current }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loadMore, activeTab]);

  return {
    items,
    loading,
    hasNextPage,
    isInitialLoading: loading && items.length === 0,
    scrollContainerRef,
    sentinelRef,
    activeTab,
    handleTabChange,
    openId,
    toggleAccordion,
    handleRequestService,
  };
}
